import axios from 'axios';

export type DecodedApiError = {
  status: number;
  tag?: string;
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
};

export type ReferenceField =
  | 'extras'
  | 'insurances'
  | 'groupId'
  | 'stationId'
  | 'catalogModelId'
  | 'vehicleId'
  | 'blockTypeId'
  | 'companyId'
  | 'entityId'
  | 'seasonId';

export type InvalidReferenceError = {
  tag: string;
  field: ReferenceField;
  id: string;
  entityType?: string;
  message: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const nonemptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

export const decodeApiError = (error: unknown): DecodedApiError | null => {
  if (!axios.isAxiosError(error) || !error.response) return null;
  const { status, data } = error.response;
  const body = isRecord(data) ? (isRecord(data.error) ? data.error : data) : {};
  const tag = nonemptyString(body._tag);
  const code = nonemptyString(body.code);
  const decoded: DecodedApiError = { status, ...(tag ? { tag } : {}), ...(code ? { code } : {}) };
  if (status >= 500 && status <= 599 && status !== 503) return decoded;
  const message = nonemptyString(body.message) ?? nonemptyString(body.error);
  if (message) decoded.message = message;
  if (isRecord(body.details)) decoded.details = body.details;
  return decoded;
};

const referenceContracts: Record<string, { fields: ReadonlyArray<ReferenceField>; idKey: string }> =
  {
    InvalidExtraReferenceError: { fields: ['extras'], idKey: 'extraId' },
    InvalidInsuranceReferenceError: { fields: ['insurances'], idKey: 'insuranceId' },
    InvalidVehicleReferenceError: {
      fields: ['groupId', 'stationId', 'catalogModelId', 'vehicleId'],
      idKey: 'id',
    },
    InvalidBlockTypeReferenceError: { fields: ['blockTypeId'], idKey: 'id' },
    InvalidCompanyReferenceError: { fields: ['companyId'], idKey: 'id' },
    InvalidGroupReferenceError: { fields: ['groupId'], idKey: 'id' },
    InvalidEntityReferenceError: { fields: ['entityId'], idKey: 'id' },
    InvalidSeasonReferenceError: { fields: ['seasonId'], idKey: 'id' },
  };

export const decodeInvalidReferenceError = (error: unknown): InvalidReferenceError | null => {
  const decoded = decodeApiError(error);
  if (decoded?.status !== 400 || !decoded.tag || !decoded.details) return null;
  const contract = Object.prototype.hasOwnProperty.call(referenceContracts, decoded.tag)
    ? referenceContracts[decoded.tag]
    : undefined;
  const field = decoded.details.field;
  if (!contract || !contract.fields.includes(field as ReferenceField)) return null;
  const id =
    nonemptyString(decoded.details[contract.idKey]) ??
    (decoded.tag === 'InvalidCompanyReferenceError'
      ? nonemptyString(decoded.details.companyId)
      : undefined);
  if (!id) return null;
  const entityType = nonemptyString(decoded.details.entityType);
  return {
    tag: decoded.tag,
    field: field as ReferenceField,
    id,
    ...(entityType ? { entityType } : {}),
    message: decoded.message ?? 'This selection is no longer available. Please choose another.',
  };
};
